import { NewBornBirthState, NewBornDeliveryType, NewBornFilterDTO } from '../../dto/newborn/NewBornDTO';
import { toISOString } from '../../utils/localeHelpers';

/**
 * Filtros de la pantalla «Bebés recién nacidos» que no son de columna: el período y los de «Más filtros». Viajan al
 * backend en `filters` (los mismos en el listado, el resumen y la exportación); los de columna de la barra «+ Filtro»
 * viajan aparte, en el `loadConfigDTO` de la grilla.
 */

/** Los filtros de «Más filtros» (todos opcionales: `null` es «Todos»). */
export interface NewBornSelectFilters {
    serviceId: number | null;
    deliveryType: NewBornDeliveryType | null;
    birthState: NewBornBirthState | null;
    sexId: number | null;
    alert: boolean | null;
    needsReview: boolean | null;
    placementId: number | null;
    financierId: number | null;
}

export type NewBornFilterField = keyof NewBornSelectFilters;

/** Los filtros de «Más filtros», en el orden de la pantalla (y de los chips). */
export const NEWBORN_MORE_FILTERS: readonly NewBornFilterField[] = [
    'serviceId', 'deliveryType', 'birthState', 'sexId', 'alert', 'needsReview', 'placementId', 'financierId',
];

/** Todos los combos sin elegir. */
export const NO_SELECT_FILTERS: NewBornSelectFilters = {
    serviceId: null,
    deliveryType: null,
    birthState: null,
    sexId: null,
    alert: null,
    needsReview: null,
    placementId: null,
    financierId: null,
};

/** Lo que hay en los filtros de la pantalla; las fechas pueden estar vacías mientras se escriben. */
export interface NewBornFiltersState extends NewBornSelectFilters {
    from: Date | null;
    to: Date | null;
}

const isoDay = (date: Date): string => toISOString(date)!.substring(0, 10);

/** Los filtros validados (con las dos fechas) listos para aplicar; `null` si falta alguna fecha. Los combos sin elegir no viajan. */
export function toAppliedFilters(filters: NewBornFiltersState): NewBornFilterDTO | null {
    if (!filters.from || !filters.to) return null;
    const applied: NewBornFilterDTO = { from: isoDay(filters.from), to: isoDay(filters.to) };
    if (filters.serviceId !== null) applied.serviceId = filters.serviceId;
    if (filters.deliveryType !== null) applied.deliveryType = filters.deliveryType;
    if (filters.birthState !== null) applied.birthState = filters.birthState;
    if (filters.sexId !== null) applied.sexId = filters.sexId;
    if (filters.alert !== null) applied.alert = filters.alert;
    if (filters.needsReview !== null) applied.needsReview = filters.needsReview;
    if (filters.placementId !== null) applied.placementId = filters.placementId;
    if (filters.financierId !== null) applied.financierId = filters.financierId;
    return applied;
}

export const sameApplied = (a: NewBornFilterDTO, b: NewBornFilterDTO): boolean =>
    a.from === b.from && a.to === b.to
    && NEWBORN_MORE_FILTERS.every((field) => a[field] === b[field]);

/** Lo que identifica una consulta (período y filtros de la pantalla): clave del caché de las secciones que se piden aparte. */
export const filtersKey = (filters: NewBornFilterDTO): string => [
    filters.from, filters.to, ...NEWBORN_MORE_FILTERS.map((field) => filters[field]),
].join('|');
